import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaArrowDown, FaArrowUp, FaPlus, FaTrash } from "react-icons/fa";
import { ProspectStatus } from "../../../models/Lead";
import {
  deleteStatus,
  getStatuses,
  saveStatus,
  updateStatus,
} from "../../../services/crmService";
import { crm } from "./crmTheme";

const PALETTE = [
  "#3182ce",
  "#38a169",
  "#dd6b20",
  "#805ad5",
  "#e53e3e",
  "#319795",
  "#d69e2e",
];

const StatusManager = ({
  isOpen,
  onClose,
  onChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const toast = useToast();
  const [statuses, setStatuses] = useState<ProspectStatus[]>([]);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const data = await getStatuses();
    setStatuses(data);
  };

  useEffect(() => {
    if (isOpen) {
      load();
      setNewName("");
    }
  }, [isOpen]);

  const add = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    const color = PALETTE[statuses.length % PALETTE.length];
    await saveStatus({ name: newName.trim(), order: statuses.length, color });
    setNewName("");
    await load();
    onChanged();
    setLoading(false);
  };

  const rename = (id: string | undefined, name: string) => {
    setStatuses((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name } : s))
    );
  };

  const toggleClosed = async (id: string | undefined) => {
    const updated = statuses.map((s) =>
      s.id === id ? { ...s, isClosed: !s.isClosed } : { ...s, isClosed: false }
    );
    setStatuses(updated);
    await Promise.all(updated.map((s) => updateStatus(s)));
    onChanged();
  };

  const persist = async (status: ProspectStatus) => {
    await updateStatus(status);
    onChanged();
  };

  const remove = async (id?: string) => {
    if (!id) return;
    await deleteStatus(id);
    await load();
    onChanged();
    toast({ title: "Status removido.", status: "info", duration: 2000 });
  };

  const swap = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= statuses.length) return;
    const reordered = [...statuses];
    [reordered[index], reordered[target]] = [
      reordered[target],
      reordered[index],
    ];
    const withOrder = reordered.map((s, i) => ({ ...s, order: i }));
    setStatuses(withOrder);
    await Promise.all(withOrder.map((s) => updateStatus(s)));
    onChanged();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="700" fontSize="md" color={crm.text}>
              Gerenciar status do funil
            </Text>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color={crm.textMuted} mb="4px">
            As colunas do funil são os status. Crie, renomeie, reordene ou
            remova as etapas do seu processo.
          </Text>
          <Text fontSize="xs" color={crm.textFaint} mb="14px">
            ✓ = etapa de fechamento (conta como lead fechado no dashboard)
          </Text>

          <Flex gap="8px" mb="16px">
            <Input
              placeholder="Nome do novo status (ex: Prospecção, Reunião...)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              h="42px"
              borderColor={crm.border}
              borderRadius="10px"
              _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
              fontSize="sm"
            />
            <Button
              leftIcon={<FaPlus />}
              bg={crm.brand}
              color="white"
              _hover={{ bg: crm.brandDark }}
              borderRadius="10px"
              onClick={add}
              isLoading={loading}
              minW="100px"
            >
              Criar
            </Button>
          </Flex>

          {statuses.map((s, index) => (
            <Flex
              key={s.id}
              align="center"
              gap="8px"
              p="10px"
              mb="8px"
              border={`1px solid ${crm.border}`}
              borderRadius="10px"
              bg={crm.surface}
              _hover={{ borderColor: crm.borderStrong }}
              transition="border .1s"
            >
              {/* Color dot — clickable swatch */}
              <Tooltip label="Cor do status" placement="top">
                <Box
                  w="14px"
                  h="14px"
                  borderRadius="full"
                  bg={s.color || crm.brand}
                  flexShrink={0}
                  cursor="pointer"
                />
              </Tooltip>
              <Input
                size="sm"
                value={s.name}
                onChange={(e) => rename(s.id, e.target.value)}
                onBlur={() => persist(s)}
                variant="flushed"
                fontWeight="500"
                color={crm.text}
                borderColor="transparent"
                _focus={{ borderColor: crm.brand }}
                fontSize="sm"
              />
              <Tooltip label="Marcar como etapa de fechamento (ganho)" placement="top">
                <Checkbox
                  isChecked={!!s.isClosed}
                  onChange={() => toggleClosed(s.id)}
                  colorScheme="green"
                  size="sm"
                  flexShrink={0}
                />
              </Tooltip>
              <IconButton
                aria-label="Subir"
                icon={<FaArrowUp />}
                size="xs"
                variant="ghost"
                color={crm.textMuted}
                _hover={{ color: crm.text }}
                isDisabled={index === 0}
                onClick={() => swap(index, -1)}
              />
              <IconButton
                aria-label="Descer"
                icon={<FaArrowDown />}
                size="xs"
                variant="ghost"
                color={crm.textMuted}
                _hover={{ color: crm.text }}
                isDisabled={index === statuses.length - 1}
                onClick={() => swap(index, 1)}
              />
              <IconButton
                aria-label="Remover"
                icon={<FaTrash />}
                size="xs"
                variant="ghost"
                color={crm.textMuted}
                _hover={{ color: crm.danger, bg: "#fef2f2" }}
                onClick={() => remove(s.id)}
              />
            </Flex>
          ))}

          {statuses.length === 0 && (
            <Text color={crm.textFaint} fontSize="sm" textAlign="center" py="20px">
              Nenhum status criado ainda.
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Button
            onClick={onClose}
            variant="ghost"
            color={crm.textMuted}
            borderRadius="10px"
          >
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StatusManager;
